'use client'

import { useEffect, useState } from 'react'
import { useAuth } from '@/store/hooks'
import {
    getHealthPlan,
    createHealthPlan,
    updateHealthPlan,
    type HealthPlanSettlementMode,
} from '@/lib/api/medical/health-plan-api'
import { toast } from 'react-toastify'
import { MdArrowBack, MdHealthAndSafety, MdSave } from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'

interface FormData {
    name: string
    ansCode: string
    cnpj: string
    phone: string
    email: string
    website: string
    planType: string
    coverage: string[]
    settlementMode: HealthPlanSettlementMode
    invoiceClosingDay: string
    paymentTermDays: string
    street: string
    number: string
    complement: string
    neighborhood: string
    city: string
    state: string
    zipCode: string
    contactName: string
    contactPhone: string
    contactEmail: string
    notes: string
    active: boolean
}

const defaultFormData: FormData = {
    name: '',
    ansCode: '',
    cnpj: '',
    phone: '',
    email: '',
    website: '',
    planType: '',
    coverage: [],
    settlementMode: 'DIRECT_INVOICE',
    invoiceClosingDay: '',
    paymentTermDays: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    zipCode: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    notes: '',
    active: true,
}

const PLAN_TYPES = [
    { value: '', label: 'Selecione...' },
    { value: 'individual', label: 'Individual' },
    { value: 'empresarial', label: 'Empresarial' },
    { value: 'coletivo_adesao', label: 'Coletivo por Adesão' },
]

const COVERAGES = [
    { value: 'ambulatorial', label: 'Ambulatorial' },
    { value: 'hospitalar', label: 'Hospitalar' },
    { value: 'obstetrícia', label: 'Obstetrícia' },
    { value: 'odontológica', label: 'Odontológica' },
    { value: 'referência', label: 'Referência' },
]

const STATES = [
    'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA',
    'PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',
]

const formatPhone = (value: string): string => {
    const numbers = value.replace(/\D/g, '')
    if (numbers.length <= 11) {
        if (numbers.length <= 10) {
            return numbers
                .replace(/^(\d{2})(\d)/, '($1) $2')
                .replace(/(\d{4})(\d)/, '$1-$2')
        }
        return numbers
            .replace(/^(\d{2})(\d)/, '($1) $2')
            .replace(/(\d{5})(\d)/, '$1-$2')
    }
    return value
}

const formatCNPJ = (value: string): string => {
    const numbers = value.replace(/\D/g, '')
    if (numbers.length <= 14) {
        return numbers
            .replace(/^(\d{2})(\d)/, '$1.$2')
            .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
            .replace(/\.(\d{3})(\d)/, '.$1/$2')
            .replace(/(\d{4})(\d)/, '$1-$2')
    }
    return value
}

const formatCEP = (value: string): string => {
    const numbers = value.replace(/\D/g, '')
    if (numbers.length <= 8) {
        return numbers.replace(/^(\d{5})(\d)/, '$1-$2')
    }
    return value
}

const unMaskValue = (value: string): string => {
    return value.replace(/\D/g, '')
}

export default function HealthPlanFormPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id

    const [formData, setFormData] = useState<FormData>(defaultFormData)
    const [loading, setLoading] = useState(false)
    const [loadingData, setLoadingData] = useState(false)
    const [loadingCep, setLoadingCep] = useState(false)

    useEffect(() => {
        if (isEditing && id) {
            loadHealthPlan(parseInt(id, 10))
        }
    }, [id])

    const loadHealthPlan = async (planId: number) => {
        setLoadingData(true)
        try {
            const response = await getHealthPlan(planId)
            if (response.success && response.result) {
                const plan = response.result
                setFormData({
                    name: plan.name || '',
                    ansCode: plan.ansCode || '',
                    cnpj: plan.cnpj ? formatCNPJ(plan.cnpj) : '',
                    phone: plan.phone ? formatPhone(plan.phone) : '',
                    email: plan.email || '',
                    website: plan.website || '',
                    planType: plan.planType || '',
                    coverage: Array.isArray(plan.coverage) ? plan.coverage : [],
                    settlementMode: plan.settlementMode || 'DIRECT_INVOICE',
                    invoiceClosingDay:
                        plan.invoiceClosingDay != null
                            ? String(plan.invoiceClosingDay)
                            : '',
                    paymentTermDays:
                        plan.paymentTermDays != null
                            ? String(plan.paymentTermDays)
                            : '',
                    street: plan.street || '',
                    number: plan.number || '',
                    complement: plan.complement || '',
                    neighborhood: plan.neighborhood || '',
                    city: plan.city || '',
                    state: plan.state || '',
                    zipCode: plan.zipCode ? formatCEP(plan.zipCode) : '',
                    contactName: plan.contactName || '',
                    contactPhone: plan.contactPhone ? formatPhone(plan.contactPhone) : '',
                    contactEmail: plan.contactEmail || '',
                    notes: plan.notes || '',
                    active: plan.active,
                })
            } else {
                toast.error('Erro ao carregar convênio')
                navigate('/registrations/convenios')
            }
        } catch {
            toast.error('Erro ao carregar convênio')
            navigate('/registrations/convenios')
        } finally {
            setLoadingData(false)
        }
    }

    const fetchAddressByCep = async (cep: string) => {
        const cleanCep = cep.replace(/\D/g, '')
        if (cleanCep.length !== 8) return

        setLoadingCep(true)
        try {
            const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`)
            const data = await response.json()

            if (!data.erro) {
                setFormData(prev => ({
                    ...prev,
                    street: data.logradouro || prev.street,
                    neighborhood: data.bairro || prev.neighborhood,
                    city: data.localidade || prev.city,
                    state: data.uf || prev.state,
                    complement: data.complemento || prev.complement,
                }))
            } else {
                toast.error('CEP não encontrado')
            }
        } catch {
            toast.error('Erro ao buscar CEP')
        } finally {
            setLoadingCep(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.name.trim()) {
            toast.error('Informe o nome do convênio')
            return
        }

        if (!user?.organizationId) return

        setLoading(true)
        try {
            const baseData = {
                name: formData.name.trim(),
                ansCode: formData.ansCode.trim() || undefined,
                cnpj: unMaskValue(formData.cnpj) || undefined,
                phone: unMaskValue(formData.phone) || undefined,
                email: formData.email.trim() || undefined,
                website: formData.website.trim() || undefined,
                planType: formData.planType || undefined,
                coverage: formData.coverage.length > 0 ? formData.coverage : undefined,
                settlementMode: formData.settlementMode,
                invoiceClosingDay: formData.invoiceClosingDay
                    ? parseInt(formData.invoiceClosingDay, 10)
                    : undefined,
                paymentTermDays: formData.paymentTermDays
                    ? parseInt(formData.paymentTermDays, 10)
                    : undefined,
                street: formData.street.trim() || undefined,
                number: formData.number.trim() || undefined,
                complement: formData.complement.trim() || undefined,
                neighborhood: formData.neighborhood.trim() || undefined,
                city: formData.city.trim() || undefined,
                state: formData.state || undefined,
                zipCode: unMaskValue(formData.zipCode) || undefined,
                contactName: formData.contactName.trim() || undefined,
                contactPhone: unMaskValue(formData.contactPhone) || undefined,
                contactEmail: formData.contactEmail.trim() || undefined,
                notes: formData.notes.trim() || undefined,
                active: formData.active,
            }

            if (isEditing && id) {
                const response = await updateHealthPlan(parseInt(id, 10), baseData)
                if (response.success) {
                    toast.success('Convênio atualizado com sucesso!')
                    navigate('/registrations/convenios')
                } else {
                    toast.error(response.error || 'Erro ao atualizar convênio')
                }
            } else {
                const response = await createHealthPlan({
                    ...baseData,
                    organizationId: user.organizationId,
                })
                if (response.success) {
                    toast.success('Convênio cadastrado com sucesso!')
                    navigate('/registrations/convenios')
                } else {
                    toast.error(response.error || 'Erro ao cadastrar convênio')
                }
            }
        } catch {
            toast.error('Erro ao salvar convênio')
        } finally {
            setLoading(false)
        }
    }

    const setField = (field: keyof FormData, value: string | boolean) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }

    if (loadingData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando...</p>
            </div>
        )
    }

    return (
        <div className="p-6 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
                <button
                    onClick={() => navigate('/registrations/convenios')}
                    className="btn btn-ghost btn-sm btn-square"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="p-3 bg-secondary/10 rounded-xl">
                    <MdHealthAndSafety className="text-3xl text-secondary" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-base-content">
                        {isEditing ? 'Editar Convênio' : 'Novo Convênio'}
                    </h1>
                    <p className="text-sm text-base-content/60">
                        {isEditing
                            ? 'Atualize os dados do convênio'
                            : 'Preencha os dados do novo convênio'}
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                {/* Dados Gerais */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6 mb-6">
                    <h2 className="text-lg font-semibold mb-4">Dados Gerais</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control w-full md:col-span-2">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Nome do Convênio{' '}
                                    <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Ex: Unimed, Bradesco Saúde..."
                                value={formData.name}
                                onChange={e => setField('name', e.target.value)}
                                required
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Código ANS
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Código ANS"
                                value={formData.ansCode}
                                onChange={e =>
                                    setField('ansCode', e.target.value)
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    CNPJ
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="00.000.000/0000-00"
                                value={formData.cnpj}
                                onChange={e =>
                                    setFormData(prev => ({
                                        ...prev,
                                        cnpj: formatCNPJ(e.target.value),
                                    }))
                                }
                                maxLength={18}
                            />
                        </div>

                        <div className="form-control">
                            <label className="label cursor-pointer justify-start gap-3">
                                <input
                                    type="checkbox"
                                    className="toggle toggle-secondary"
                                    checked={formData.active}
                                    onChange={e =>
                                        setField('active', e.target.checked)
                                    }
                                />
                                <span className="label-text font-medium">
                                    Convênio Ativo
                                </span>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Tipo e Cobertura */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6 mb-6">
                    <h2 className="text-lg font-semibold mb-4">
                        Tipo e Cobertura
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Tipo de Plano
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={formData.planType}
                                onChange={e =>
                                    setField('planType', e.target.value)
                                }
                            >
                                {PLAN_TYPES.map(pt => (
                                    <option key={pt.value} value={pt.value}>
                                        {pt.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Cobertura
                                </span>
                            </label>
                            <div className="flex flex-wrap gap-4 mt-1">
                                {COVERAGES.map(c => (
                                    <label
                                        key={c.value}
                                        className="label cursor-pointer gap-2"
                                    >
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-secondary checkbox-sm"
                                            checked={formData.coverage.includes(
                                                c.value
                                            )}
                                            onChange={e => {
                                                const next = e.target.checked
                                                    ? [
                                                          ...formData.coverage,
                                                          c.value,
                                                      ]
                                                    : formData.coverage.filter(
                                                          v => v !== c.value
                                                      )
                                                setFormData(prev => ({
                                                    ...prev,
                                                    coverage: next,
                                                }))
                                            }}
                                        />
                                        <span className="label-text">
                                            {c.label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Faturamento: como este convênio paga os atendimentos que cobre. */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-1">
                        Faturamento
                    </h2>
                    <p className="text-sm text-base-content/60 mb-5">
                        Atendimentos cobertos por este convênio geram cobrança
                        com ele como pagador — sem link de pagamento ao paciente.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        <div className="form-control w-full md:col-span-3">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Regime
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={formData.settlementMode}
                                onChange={e =>
                                    setField(
                                        'settlementMode',
                                        e.target
                                            .value as HealthPlanSettlementMode
                                    )
                                }
                            >
                                <option value="DIRECT_INVOICE">
                                    Fatura direta — fecha a competência e cobra o
                                    convênio
                                </option>
                                <option value="TISS_BATCH">
                                    Lote TISS (operadora ANS) — ainda não
                                    suportado
                                </option>
                            </select>
                            {formData.settlementMode === 'TISS_BATCH' && (
                                <div className="alert alert-warning mt-3">
                                    <span className="text-sm">
                                        O faturamento por lote TISS (guia + XML +
                                        retorno da operadora) ainda não está
                                        implementado. Atendimentos deste convênio
                                        serão recusados na geração da cobrança.
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Dia do fechamento
                                </span>
                            </label>
                            <input
                                type="number"
                                min={1}
                                max={28}
                                className="input input-bordered w-full"
                                placeholder="Ex: 25"
                                value={formData.invoiceClosingDay}
                                onChange={e =>
                                    setField('invoiceClosingDay', e.target.value)
                                }
                            />
                            <span className="text-xs text-base-content/50 mt-1">
                                Vazio usa o último dia do mês.
                            </span>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Prazo de pagamento (dias)
                                </span>
                            </label>
                            <input
                                type="number"
                                min={0}
                                max={365}
                                className="input input-bordered w-full"
                                placeholder="Ex: 30"
                                value={formData.paymentTermDays}
                                onChange={e =>
                                    setField('paymentTermDays', e.target.value)
                                }
                            />
                            <span className="text-xs text-base-content/50 mt-1">
                                Define o vencimento da cobrança. Vazio faz a
                                cobrança vencer no dia do atendimento.
                            </span>
                        </div>
                    </div>
                </div>

                {/* Contato Principal */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6 mb-6">
                    <h2 className="text-lg font-semibold mb-4">
                        Contato Principal
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Telefone
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="(00) 00000-0000"
                                value={formData.phone}
                                onChange={e =>
                                    setFormData(prev => ({
                                        ...prev,
                                        phone: formatPhone(e.target.value),
                                    }))
                                }
                                maxLength={15}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Email
                                </span>
                            </label>
                            <input
                                type="email"
                                className="input input-bordered w-full"
                                placeholder="contato@convenio.com.br"
                                value={formData.email}
                                onChange={e =>
                                    setField('email', e.target.value)
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Website
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="https://www.convenio.com.br"
                                value={formData.website}
                                onChange={e =>
                                    setField('website', e.target.value)
                                }
                            />
                        </div>
                    </div>
                </div>

                {/* Contato Adicional */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6 mb-6">
                    <h2 className="text-lg font-semibold mb-4">
                        Contato Adicional
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Nome do Contato
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Nome"
                                value={formData.contactName}
                                onChange={e =>
                                    setField('contactName', e.target.value)
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Telefone do Contato
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="(00) 00000-0000"
                                value={formData.contactPhone}
                                onChange={e =>
                                    setFormData(prev => ({
                                        ...prev,
                                        contactPhone: formatPhone(e.target.value),
                                    }))
                                }
                                maxLength={15}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Email do Contato
                                </span>
                            </label>
                            <input
                                type="email"
                                className="input input-bordered w-full"
                                placeholder="contato@email.com"
                                value={formData.contactEmail}
                                onChange={e =>
                                    setField('contactEmail', e.target.value)
                                }
                            />
                        </div>
                    </div>
                </div>

                {/* Endereço */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6 mb-6">
                    <h2 className="text-lg font-semibold mb-4">Endereço</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    CEP
                                </span>
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="00000-000"
                                    value={formData.zipCode}
                                    onChange={e => {
                                        const formatted = formatCEP(e.target.value)
                                        setFormData(prev => ({
                                            ...prev,
                                            zipCode: formatted,
                                        }))
                                        fetchAddressByCep(formatted)
                                    }}
                                    maxLength={9}
                                />
                                {loadingCep && (
                                    <span className="loading loading-spinner loading-sm absolute right-3 top-1/2 -translate-y-1/2"></span>
                                )}
                            </div>
                        </div>

                        <div className="form-control w-full md:col-span-2">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Rua
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Rua, Avenida..."
                                value={formData.street}
                                onChange={e =>
                                    setField('street', e.target.value)
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Número
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Nº"
                                value={formData.number}
                                onChange={e =>
                                    setField('number', e.target.value)
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Complemento
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Sala, Andar..."
                                value={formData.complement}
                                onChange={e =>
                                    setField('complement', e.target.value)
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Bairro
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Bairro"
                                value={formData.neighborhood}
                                onChange={e =>
                                    setField('neighborhood', e.target.value)
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Cidade
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Cidade"
                                value={formData.city}
                                onChange={e =>
                                    setField('city', e.target.value)
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Estado
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={formData.state}
                                onChange={e =>
                                    setField('state', e.target.value)
                                }
                            >
                                <option value="">Selecione...</option>
                                {STATES.map(s => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Observações */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6 mb-6">
                    <h2 className="text-lg font-semibold mb-4">Observações</h2>
                    <div className="form-control w-full">
                        <textarea
                            className="textarea textarea-bordered w-full h-32"
                            placeholder="Observações gerais sobre o convênio..."
                            value={formData.notes}
                            onChange={e => setField('notes', e.target.value)}
                        />
                    </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3">
                    <button
                        type="button"
                        onClick={() => navigate('/registrations/convenios')}
                        className="btn btn-ghost"
                        disabled={loading}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="loading loading-spinner loading-sm"></span>
                                Salvando...
                            </>
                        ) : (
                            <>
                                <MdSave className="text-lg" />
                                {isEditing ? 'Atualizar' : 'Cadastrar'}
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}
